import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Loader2, ChevronRight, ShieldCheck, Rocket, Timer, Volume2, Ruler, Flame, Zap, ShoppingBag, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { GlassCard } from "../components/common/ui";
import { StockBadge, formatGBP } from "../components/common/StockBadge";
import ProductCard from "../components/common/ProductCard";
import Breadcrumbs from "../components/seo/Breadcrumbs";
import ContentSections from "../components/public/ContentSections";
import RelatedContent from "../components/public/RelatedContent";
import { Button } from "../components/ui/button";
import { useShop } from "../context/ShopContext";
import AgeBadge from "../components/common/AgeBadge";
import api from "../lib/api";
import { bestImg, onImgError } from "../lib/img";

function ytEmbed(url) {
  if (!url) return null;
  let id = "";
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, "");
    if (host === "youtu.be") id = u.pathname.split("/")[1] || "";
    else if (u.pathname.startsWith("/shorts/")) id = u.pathname.split("/")[2] || "";
    else if (u.pathname.startsWith("/embed/")) id = u.pathname.split("/")[2] || "";
    else if (u.pathname.startsWith("/v/")) id = u.pathname.split("/")[2] || "";
    else id = u.searchParams.get("v") || "";
  } catch {
    const m = url.match(/[\w-]{11}/);
    id = m ? m[0] : "";
  }
  id = (id || "").slice(0, 11);
  return /^[\w-]{11}$/.test(id) ? `https://www.youtube.com/embed/${id}` : null;
}
function vimeoEmbed(url) {
  if (!url) return null;
  const m = url.match(/vimeo\.com\/(\d+)/);
  return m ? `https://player.vimeo.com/video/${m[1]}` : null;
}

export default function ProductDetail() {
  const { slug } = useParams();
  const { addToBasket } = useShop();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [active, setActive] = useState(0);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    setLoading(true); setNotFound(false); setActive(0);
    api.get(`/products/${slug}`).then(({ data }) => {
      setP(data);
    }).catch(() => setNotFound(true)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="py-32 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>;
  if (notFound) return (
    <div className="mx-auto max-w-3xl px-4 py-32 text-center" data-testid="product-not-found">
      <h1 className="font-playfair text-3xl text-white">Product not found</h1>
      <Link to="/fireworks" className="mt-4 inline-block text-gold underline">Browse all fireworks</Link>
    </div>
  );

  const images = p.images || [];
  const specs = p.specs || {};
  const embed = ytEmbed(p.youtube_url) || vimeoEmbed(p.vimeo_url);
  const specRows = [
    ["Firework category", specs.firework_category, Flame], ["Consumer category", specs.classification, Zap],
    ["Shots", specs.shots, Rocket], ["Duration", specs.duration_seconds != null ? `${specs.duration_seconds}s` : null, Timer],
    ["Noise level", specs.noise_level, Volume2], ["Safety distance", specs.safety_distance_m != null ? `${specs.safety_distance_m}m` : null, Ruler],
  ].filter(([, v]) => v != null && v !== "");
  const savings = p.compare_at_price && p.compare_at_price > p.retail_price ? p.compare_at_price - p.retail_price : 0;
  const promoPP = p.promotion?.pricing;

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12" data-testid="product-detail">
      <Breadcrumbs className="mb-6" />

      <div className="grid lg:grid-cols-2 gap-10">
        <div>
          <GlassCard className="p-0 overflow-hidden aspect-square" hover={false}>
            {images[active] ? <img data-testid="product-main-image" src={bestImg(images[active])} onError={onImgError(images[active])} alt={images[active].alt || p.name} className="h-full w-full object-contain bg-black/30 p-2" /> : <div className="h-full w-full grid place-items-center text-white/20"><Rocket className="h-16 w-16" /></div>}
          </GlassCard>
          {images.length > 1 && (
            <div className="mt-3 grid grid-cols-5 gap-2" data-testid="product-gallery">
              {images.map((m, i) => (
                <button key={i} onClick={() => setActive(i)} data-testid={`gallery-thumb-${i}`} className={`aspect-square rounded-lg overflow-hidden border ${i === active ? "border-gold" : "border-white/15"}`}>
                  <img src={m.thumb_url || m.webp_url || m.url} onError={onImgError(m)} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          {p.brand && <p className="text-xs tracking-[0.2em] uppercase text-white/50">{p.brand}</p>}
          <h1 className="font-playfair text-xl sm:text-2xl text-white mt-1" data-testid="product-title">{p.name}</h1>
          {p.short_description && <p className="mt-3 text-muted-foreground leading-relaxed" data-testid="product-short-desc">{p.short_description}</p>}
          {p.description && (
            <div className="mt-3 prose prose-invert prose-sm max-w-none text-white/75" data-testid="product-description" dangerouslySetInnerHTML={{ __html: p.description }} />
          )}
          <div className="mt-4 flex items-center gap-3">
            <StockBadge status={p.stock_status} />
            <span className="text-sm text-white/50">{p.category_id ? "" : ""}</span>
          </div>
          <div className="mt-5" data-testid="product-pricing">
            {p.promotion && (
              <div className="mb-4 rounded-xl border border-gold/25 bg-gold/5 p-4" data-testid="product-promotion">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-gold text-ink px-3 py-1 text-xs font-bold uppercase tracking-wide"><Sparkles className="h-3.5 w-3.5" /> {p.promotion.ribbon}</span>
                  {p.promotion.ends_label && <span className="text-xs text-white/50">Ends {p.promotion.ends_label}</span>}
                </div>
                <p className="mt-2 text-sm text-white/80 font-medium">Current Offer — {p.promotion.name}</p>
                {p.promotion.type === "bogof" && <p className="text-sm text-white/60">Add any two eligible products — the cheapest one is FREE.</p>}
                {p.promotion.type === "bogo_half" && <p className="text-sm text-white/60">Add any two eligible products — the cheapest one is half price.</p>}
                {p.promotion.type === "b2g1f" && <p className="text-sm text-white/60">Add any three eligible products — the cheapest one is FREE.</p>}
                {p.promotion.type === "fixed_bundle" && <p className="text-sm text-white/60">Buy the bundle set together for {formatGBP(p.promotion.bundle_price)}.</p>}
                {p.promotion.description && <p className="mt-1 text-xs text-white/50">{p.promotion.description}</p>}
                <Link to="/offers" className="mt-2 inline-block text-xs font-semibold text-gold hover:underline" data-testid="view-eligible-products">View all eligible products →</Link>
              </div>
            )}
            {promoPP?.per_unit && promoPP.saving > 0 ? (
              <>
                <div className="flex items-center gap-3">
                  <span className="text-2xl sm:text-3xl font-semibold text-white/50 line-through" data-testid="product-was">Was {formatGBP(promoPP.original_price)}</span>
                  <span className="text-xs font-semibold text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-2.5 py-0.5">Save {formatGBP(promoPP.saving)}</span>
                </div>
                <div className="mt-2 flex items-baseline gap-2">
                  <span className="text-sm uppercase tracking-wide text-white/50">Offer Price</span>
                  <span className="font-playfair text-3xl sm:text-4xl text-gold leading-none" data-testid="product-price">{formatGBP(promoPP.current_price)}</span>
                </div>
              </>
            ) : (
              <>
                {p.compare_at_price && p.compare_at_price > p.retail_price && (
                  <div className="flex items-center gap-3">
                    <span className="text-2xl sm:text-3xl font-semibold text-firered line-through decoration-firered decoration-2" data-testid="product-rrp">RRP {formatGBP(p.compare_at_price)}</span>
                    {savings > 0 && <span className="text-xs font-semibold text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-2.5 py-0.5">Save {formatGBP(savings)}</span>}
                  </div>
                )}
                <div className="mt-2 flex items-baseline gap-2">
                  <span className="text-sm uppercase tracking-wide text-white/50">Our Price</span>
                  <span className="font-playfair text-3xl sm:text-4xl text-emerald-400 leading-none" data-testid="product-price">{formatGBP(p.retail_price)}</span>
                </div>
              </>
            )}
          </div>

          <div className="mt-6">
            <Button asChild={false} disabled={p.stock_status !== "in_stock"} data-testid="product-add-basket-btn"
              onClick={() => { if (p.stock_status === "in_stock") { addToBasket(p); toast.success(`${p.name} added to basket`); } }}
              className={`cf-gold-btn font-semibold w-full sm:w-auto px-10 gap-2 ${p.stock_status !== "in_stock" ? "opacity-60 cursor-not-allowed" : ""}`}>
              <ShoppingBag className="h-4 w-4" /> {p.stock_status === "in_stock" ? "Add to Basket" : "Sold Out"}
            </Button>
            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2" data-testid="product-age-notice">
              <AgeBadge size={34} linked />
              <span className="text-xs text-white/40">Pay in store on collection.</span>
            </div>
          </div>

          {specRows.length > 0 && (
            <GlassCard className="mt-6 p-5" hover={false} data-testid="product-specs">
              <h3 className="font-playfair text-lg text-white mb-3">Specifications</h3>
              <div className="grid grid-cols-2 gap-3">
                {specRows.map(([label, val, Icon]) => (
                  <div key={label} className="flex items-center gap-2 text-sm">
                    <Icon className="h-4 w-4 text-gold" />
                    <span className="text-white/50">{label}:</span>
                    <span className="text-white capitalize">{val}</span>
                  </div>
                ))}
              </div>
              {specs.effects_text && (
                <div className="mt-4 pt-3 border-t border-white/10">
                  <span className="text-white/50 text-sm">Effects: </span>
                  <span className="text-white/80 text-sm">{specs.effects_text}</span>
                </div>
              )}
            </GlassCard>
          )}
        </div>
      </div>

      {embed && (
        <div className="mt-12 max-w-3xl" data-testid="product-video">
          <h2 className="font-playfair text-2xl text-white mb-3">Demonstration video</h2>
          <div className="aspect-video rounded-xl overflow-hidden border border-white/10">
            <iframe src={embed} title="Demonstration" className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
          </div>
        </div>
      )}

      <ContentSections content={p.content_seo} />

      {p.related?.length > 0 && (
        <div className="mt-16">
          <h2 className="font-playfair text-2xl text-white mb-5">Related Products</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="related-products">
            {p.related.map((r, i) => <ProductCard key={r.product_id} product={r} index={i} />)}
          </div>
        </div>
      )}

      <RelatedContent data={p.related_content} />
    </div>
  );
}
